<template>
  <div class="page">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
      <h2 class="page-title" style="margin:0;">{{ $t('menu.tickets') }}</h2>
      <el-button type="primary" class="btn-glass" @click="openCreate">{{ $t('common.new') }}</el-button>
    </div>

    <div class="stats-grid" style="margin-top: 14px;">
      <div :class="['stats-tile', 'glass-card', !statusFilter && !disputeFilter ? 'stats-tile--active' : '']" @click="setStatus(null)">
        <div class="muted stats-tile__label">{{ $t('ticket.stats.total') }}</div>
        <div class="stats-tile__value">{{ stats.total }}</div>
      </div>
      <div :class="['stats-tile', 'glass-card', statusFilter==='PENDING' ? 'stats-tile--active' : '']" @click="setStatus('PENDING')">
        <div class="muted stats-tile__label">{{ $t('ticket.stats.pending') }}</div>
        <div class="stats-tile__value">{{ stats.pending }}</div>
      </div>
      <div :class="['stats-tile', 'glass-card', statusFilter==='IN_PROGRESS' ? 'stats-tile--active' : '']" @click="setStatus('IN_PROGRESS')">
        <div class="muted stats-tile__label">{{ $t('ticket.stats.inProgress') }}</div>
        <div class="stats-tile__value">{{ stats.inProgress }}</div>
      </div>
      <div :class="['stats-tile', 'glass-card', statusFilter==='WAITING_RESUBMIT' ? 'stats-tile--active' : '']" @click="setStatus('WAITING_RESUBMIT')">
        <div v-if="stats.waitingResubmit>0" class="stats-tile__badge">{{ badgeText(stats.waitingResubmit) }}</div>
        <div class="muted stats-tile__label">{{ $t('ticket.stats.waitingResubmit') }}</div>
        <div class="stats-tile__value">{{ stats.waitingResubmit }}</div>
      </div>
      <div :class="['stats-tile', 'glass-card', disputeFilter==='ACTIVE' ? 'stats-tile--active' : '']" @click="setDisputeFilter('ACTIVE')">
        <div class="muted stats-tile__label">{{ disputingStatsLabel }}</div>
        <div class="stats-tile__value">{{ stats.disputing }}</div>
      </div>
      <div :class="['stats-tile', 'glass-card', statusFilter==='CLOSED' ? 'stats-tile--active' : '']" @click="setStatus('CLOSED')">
        <div v-if="closedNoticeCount>0" class="stats-tile__badge">{{ badgeText(closedNoticeCount) }}</div>
        <div class="muted stats-tile__label">{{ $t('ticket.stats.closed') }}</div>
        <div class="stats-tile__value">{{ stats.closed }}</div>
      </div>
      <div :class="['stats-tile', 'glass-card', statusFilter==='CANCELED' ? 'stats-tile--active' : '']" @click="setStatus('CANCELED')">
        <div class="muted stats-tile__label">{{ $t('ticket.stats.canceled') }}</div>
        <div class="stats-tile__value">{{ stats.canceled }}</div>
      </div>
    </div>

    <div class="glass-card" style="margin-top: 14px; padding: 14px;">
      <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
        <el-input
          v-model="q"
          clearable
          :placeholder="$t('ticket.searchPlaceholder')"
          style="width: 260px;"
          @keyup.enter.native="applyFilters"
        />
        <el-date-picker
          v-model="range"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="~"
          :start-placeholder="$t('kpi.from')"
          :end-placeholder="$t('kpi.to')"
          style="width: 320px;"
        />
        <el-button type="primary" class="btn-glass" plain icon="el-icon-search" @click="applyFilters" :loading="loading">{{ $t('common.search') }}</el-button>
      </div>

      <el-table :data="items" style="width:100%; margin-top: 12px;" @row-click="openDetail">
        <el-table-column prop="caseNo" :label="$t('field.caseNo')" width="140" />
        <el-table-column prop="title" :label="$t('field.title')" />
        <el-table-column prop="status" :label="$t('field.status')" width="160">
          <template slot-scope="s">
            <span class="status-pill-wrap">
              <span
                :class="[
                  'tag-pill',
                  (s.row.status==='WAITING_RESUBMIT' || s.row.hasNewNotice) ? 'tag-pill--with-dot' : ''
                ]"
              >{{ caseStatusLabel(s.row) }}</span>
              <span v-if="s.row.status==='WAITING_RESUBMIT' || s.row.hasNewNotice" class="status-pill-dot" />
            </span>
          </template>
        </el-table-column>
        <el-table-column :label="flowColumnLabel" width="220">
          <template slot-scope="s">
            <span class="tag-pill">{{ flowLabel(s.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" :label="$t('field.createdAt')" width="190" />
      </el-table>

      <div style="display:flex;justify-content:flex-end;margin-top:12px;">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[5,10,20,50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-dialog
      :title="$t('ticket.newTicket')"
      :visible.sync="createDialog"
      width="760px"
      top="6vh"
      :append-to-body="true"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @close="onCreateClose"
    >
      <el-form :model="createForm" label-position="top">
        <el-form-item :label="$t('ticket.submitTarget')">
          <el-radio-group v-model="createForm.targetType" @change="onCreateTargetChange">
            <el-radio-button label="POOL">{{ $t('ticket.targetPool') }}</el-radio-button>
            <el-radio-button label="SECONDARY_HANDLER">{{ $t('ticket.targetSecondary') }}</el-radio-button>
          </el-radio-group>
          <div class="muted create-target-hint">{{ createTargetHint }}</div>
        </el-form-item>
        <el-form-item v-if="createForm.targetType==='SECONDARY_HANDLER'" :label="$t('ticket.secondaryHandler')">
          <el-select
            v-model="createForm.secondaryHandlerId"
            filterable
            :loading="secondaryHandlersLoading"
            :placeholder="$t('ticket.selectSecondaryHandler')"
            style="width:100%;"
          >
            <el-option
              v-for="h in secondaryHandlers"
              :key="h.id"
              :label="handlerLabel(h)"
              :value="h.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('field.title')">
          <el-input v-model="createForm.title" maxlength="200" />
        </el-form-item>
        <el-form-item :label="$t('field.message')">
          <el-input v-model="createForm.content" type="textarea" :rows="5" />
        </el-form-item>
        <el-form-item :label="$t('field.attachments')">
          <FileUploader ref="createUploader" v-model="createFiles" @uploading-change="createUploading = $event" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createDialog=false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          class="btn-glass"
          :loading="createLoading || createUploading"
          :disabled="createLoading || createUploading"
          @click="submitCreate"
        >{{ $t('common.submit') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="detailTitle"
      :visible.sync="detailDialog"
      width="92vw"
      top="4vh"
      :append-to-body="true"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="true"
      @close="onDetailClose"
      custom-class="att-preview-dialog"
    >
      <div v-if="detailLoading" class="muted">{{ $t('common.loading') }}</div>
      <div v-else-if="!detail" class="muted">-</div>
      <div v-else>
        <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ detail.title }}</div>
          <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
            <span class="tag-pill">{{ caseStatusLabel(detail) }}</span>
            <span class="tag-pill">{{ flowLabel(detail) }}</span>
          </div>
        </div>
        <div class="muted" style="margin-top:6px;">{{ detail.createdAt ? new Date(detail.createdAt).toLocaleString() : '-' }}</div>

        <div class="chat" style="margin-top: 12px;">
          <div v-for="b in detailBlocks" :key="b.key">
            <div v-if="b.type==='separator'" class="chat__separator">
              <div class="chat__separatorLine" />
              <span class="tag-pill tag-pill--warn chat__separatorTag">{{ b.label }}</span>
            </div>
            <div v-else :class="['chat__row', b.alignRight ? 'chat__row--mine' : 'chat__row--other']">
              <div :class="['chat__bubble', b.alignRight ? 'chat__bubble--mine' : 'chat__bubble--other']">
                <div class="chat__meta">
                  <span class="chat__name">{{ chatSenderLabel(b) }}</span>
                  <span class="muted" style="margin-left:auto;">{{ b.createdAt ? new Date(b.createdAt).toLocaleString() : '-' }}</span>
                </div>
                <div v-if="b.resultCode" style="margin-top:6px;">
                  <span class="tag-pill tag-pill--warn">{{ trDisputeResult(b.resultCode) }}</span>
                </div>
                <div v-if="b.text" class="chat__text">{{ b.text }}</div>
                <div v-if="b.allFileIds && b.allFileIds.length>0" style="margin-top: 10px;">
                  <AttachmentGallery :file-ids="b.allFileIds" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style="margin-top: 14px; display:flex; gap:10px; flex-wrap:wrap;">
          <el-button v-if="canUrgeCase" type="warning" plain :loading="urging" @click="doUrgeCase">{{ $t('ticket.urge') }}</el-button>
          <el-button v-if="canUrgeDispute" type="warning" plain :loading="urging" @click="doUrgeDispute">{{ $t('ticket.urgeDispute') }}</el-button>
          <el-button v-if="detail.status==='WAITING_RESUBMIT'" type="primary" class="btn-glass" @click="resubmitDialog=true">{{ $t('ticket.resubmit') }}</el-button>
          <el-button v-if="detail.canCancel" @click="cancel">{{ $t('ticket.cancelTicket') }}</el-button>

          <el-button v-if="detail.status==='CLOSED' && !detail.dispute" type="primary" class="btn-glass" @click="disputeDialog=true">{{ $t('ticket.createDispute') }}</el-button>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="detailDialog=false">{{ $t('common.cancel') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="$t('ticket.resubmit')"
      :visible.sync="resubmitDialog"
      width="720px"
      top="6vh"
      :append-to-body="true"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @close="onResubmitClose"
    >
      <el-input v-model="resubmitContent" type="textarea" :rows="6" />
      <div style="margin-top: 10px;">
        <FileUploader ref="resubmitUploader" v-model="resubmitFiles" @uploading-change="resubmitUploading = $event" />
      </div>
      <span slot="footer">
        <el-button @click="resubmitDialog=false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          class="btn-glass"
          :loading="actionLoading || resubmitUploading"
          :disabled="actionLoading || resubmitUploading"
          @click="resubmit"
        >{{ $t('common.submit') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="$t('ticket.createDispute')"
      :visible.sync="disputeDialog"
      width="720px"
      top="6vh"
      :append-to-body="true"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @close="onDisputeClose"
    >
      <el-input v-model="disputeContent" type="textarea" :rows="6" />
      <div style="margin-top: 10px;">
        <FileUploader ref="disputeUploader" v-model="disputeFiles" @uploading-change="disputeUploading = $event" />
      </div>
      <span slot="footer">
        <el-button @click="disputeDialog=false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          class="btn-glass"
          :loading="actionLoading || disputeUploading"
          :disabled="actionLoading || disputeUploading"
          @click="dispute"
        >{{ $t('common.submit') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { pageCases, createCase, listSecondaryHandlers, getCaseDetail, resubmitCase, cancelCase, createDispute, caseStats, markCaseSeen, urgeCase, urgeDispute } from '../../api/tickets';
import FileUploader from '../../components/FileUploader.vue';
import AttachmentGallery from '../../components/AttachmentGallery.vue';
import { parseMessage } from '../../utils/message';
import { caseStatusLabel as formatCaseStatusLabel, flowLabel as formatFlowLabel, localText as formatLocalText } from '../../utils/caseDisplay';

export default {
  name: 'TicketList',
  components: { FileUploader, AttachmentGallery },
  data() {
    return {
      loading: false,
      statusFilter: null,
      disputeFilter: null,
      q: '',
      range: [],
      stats: { total: 0, pending: 0, inProgress: 0, waitingResubmit: 0, disputing: 0, closed: 0, canceled: 0 },
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,
      createDialog: false,
      createLoading: false,
      createUploading: false,
      secondaryHandlersLoading: false,
      secondaryHandlers: [],
      createForm: {
        title: '',
        content: '',
        targetType: 'POOL',
        secondaryHandlerId: null
      },
      createFiles: [],
      detailDialog: false,
      detailLoading: false,
      detail: null,
      actionLoading: false,
      urging: false,
      resubmitDialog: false,
      resubmitUploading: false,
      resubmitContent: '',
      resubmitFiles: [],
      disputeDialog: false,
      disputeUploading: false,
      disputeContent: '',
      disputeFiles: []
    };
  },
  created() {
    this.loadStats();
    this.load();
    this.maybeOpenFromQuery();
    this.maybeOpenDetailFromQuery();
  },
  computed: {
    version() {
      return this.$store.state.versions.userCases;
    },
    closedNoticeCount() {
      return this.$store.state.ticketClosedNoticeCount || 0;
    },
    createFileIds() {
      return (this.createFiles || []).map((x) => (x ? x.id : null)).filter((x) => x != null);
    },
    resubmitFileIds() {
      return (this.resubmitFiles || []).map((x) => (x ? x.id : null)).filter((x) => x != null);
    },
    disputeFileIds() {
      return (this.disputeFiles || []).map((x) => (x ? x.id : null)).filter((x) => x != null);
    },
    detailTitle() {
      return this.detail ? `${this.$t('ticket.ticketNo')} ${this.detail.caseNo}` : this.$t('ticket.ticketNo');
    },
    flowColumnLabel() {
      return formatLocalText(this, '流转', 'Flow');
    },
    disputingStatsLabel() {
      return formatLocalText(this, '争议中工单数', 'Disputing');
    },
    createTargetHint() {
      return this.createForm.targetType === 'SECONDARY_HANDLER'
        ? this.$t('ticket.targetSecondaryHint')
        : this.$t('ticket.targetPoolHint');
    },
    canUrgeCase() {
      if (!this.detail) return false;
      return ['PENDING', 'PENDING_SECONDARY', 'IN_PROGRESS', 'ESCALATED', 'ESCALATED_RETURNED'].includes(this.detail.status);
    },
    canUrgeDispute() {
      const st = this.detail && this.detail.dispute ? this.detail.dispute.status : '';
      return ['DISPUTE_CREATED', 'DISPUTE_IN_PROGRESS', 'DISPUTE_SECONDARY_CREATED', 'DISPUTE_SECONDARY_IN_PROGRESS'].includes(st);
    },
    displayMessages() {
      const list = this.detail && this.detail.messages ? this.detail.messages : [];
      return list.map((m) => {
        const parsed = parseMessage(m && m.content ? m.content : '');
        const ids = [];
        const seen = new Set();
        const add = (x) => {
          if (x == null) return;
          const n = Number(x);
          if (!n || seen.has(n)) return;
          seen.add(n);
          ids.push(n);
        };
        (m && m.fileIds ? m.fileIds : []).forEach(add);
        (parsed.fileIds || []).forEach(add);
        return {
          ...m,
          mine: !!(m && m.mine),
          alignRight: !!(m && m.mine),
          text: parsed.text,
          resultCode: parsed.resultCode,
          allFileIds: ids
        };
      });
    },
    detailBlocks() {
      const ms = this.displayMessages || [];
      const blocks = [];
      let inserted = false;
      const hasDispute = !!(this.detail && this.detail.dispute);
      const disputeLabel = hasDispute
        ? `${this.$t('ticket.disputeLabel')}：${this.$t(`status.dispute.${this.detail.dispute.status}`)}`
        : '';

      ms.forEach((m, idx) => {
        if (hasDispute && !inserted && m && String(m.phase || '') === 'DISPUTE') {
          blocks.push({ type: 'separator', key: `sep_dispute_${this.detail.dispute.id || 'x'}`, label: disputeLabel });
          inserted = true;
        }
        blocks.push({ ...m, type: 'message', key: `m_${m.id != null ? m.id : idx}` });
      });

      if (hasDispute && !inserted) {
        blocks.push({ type: 'separator', key: `sep_dispute_${this.detail.dispute.id || 'x'}`, label: disputeLabel });
      }
      return blocks;
    }
  },
  watch: {
    version() {
      this.loadStats();
      this.load();
      if (this.detailDialog && this.detail && this.detail.caseNo) {
        this.loadDetail(this.detail.caseNo);
      }
    },
    '$route.query.create'() {
      this.maybeOpenFromQuery();
    },
    '$route.query.caseNo'() {
      this.maybeOpenDetailFromQuery();
    }
  },
  methods: {
    caseStatusLabel(row) {
      return formatCaseStatusLabel(this, row);
    },
    flowLabel(row) {
      return formatFlowLabel(this, row);
    },
    chatSenderLabel(b) {
      if (!b) return '-';
      return b.senderName || '-';
    },
    trDisputeResult(code) {
      const map = { OK: 'RESOLVED', RESOLVED: 'RESOLVED', REJECT: 'REJECTED', REJECTED: 'REJECTED' };
      const c = map[code] || code;
      const k = `dispute.result.${c}`;
      try {
        if (this.$i18n && this.$i18n.te && this.$i18n.te(k)) return this.$t(k);
      } catch (e) {}
      return c;
    },
    badgeText(n) {
      const num = Number(n || 0);
      return num > 9 ? '9+' : String(num);
    },
    setStatus(st) {
      this.statusFilter = st;
      this.disputeFilter = null;
      this.page = 1;
      this.load();
    },
    setDisputeFilter(st) {
      this.disputeFilter = st;
      this.statusFilter = null;
      this.page = 1;
      this.load();
    },
    maybeOpenFromQuery() {
      if (String(this.$route.query.create || '') === '1') {
        this.createDialog = true;
      }
    },
    maybeOpenDetailFromQuery() {
      const caseNo = this.$route.query.caseNo ? String(this.$route.query.caseNo) : '';
      if (!caseNo) return;
      this.openDetail({ caseNo });
    },
    async loadStats() {
      try {
        this.stats = await caseStats();
        try {
          this.$store.commit('setTicketNeedMoreCount', this.stats && this.stats.waitingResubmit ? this.stats.waitingResubmit : 0);
          this.$store.commit(
            'setTicketClosedNoticeCount',
            ((this.stats && this.stats.closedNotice) || 0) + ((this.stats && this.stats.disputeDecisionNotice) || 0)
          );
        } catch (e) {}
      } catch (e) {}
    },
    async load() {
      this.loading = true;
      try {
        const from = this.range && this.range.length === 2 ? this.range[0] : undefined;
        const to = this.range && this.range.length === 2 ? this.range[1] : undefined;
        const r = await pageCases({
          status: this.statusFilter || undefined,
          dispute: this.disputeFilter || undefined,
          q: this.q || undefined,
          from,
          to,
          page: this.page - 1,
          size: this.pageSize
        });
        this.items = (r.items || []).map((x) => ({
          ...x,
          createdAt: x.createdAt ? new Date(x.createdAt).toLocaleString() : '-'
        }));
        this.total = r.total || 0;
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.page = 1;
      this.load();
    },
    onPageChange(p) {
      this.page = p;
      this.load();
    },
    onSizeChange(s) {
      this.pageSize = s;
      this.page = 1;
      this.load();
    },
    openCreate() {
      this.createDialog = true;
      this.ensureSecondaryHandlers();
      if (String(this.$route.query.create || '') !== '1') {
        this.$router.replace({ path: '/tickets', query: { ...this.$route.query, create: '1' } });
      }
    },
    onCreateClose() {
      // Clear URL state so refresh won't auto-open.
      if (String(this.$route.query.create || '') === '1') {
        const q = { ...this.$route.query };
        delete q.create;
        this.$router.replace({ path: '/tickets', query: q });
      }
      // Also reset draft state and cancel in-flight uploads to avoid attachments leaking into the next ticket.
      try {
        if (this.$refs.createUploader && this.$refs.createUploader.cancelUploads) this.$refs.createUploader.cancelUploads();
      } catch (e) {}
      this.createForm = { title: '', content: '', targetType: 'POOL', secondaryHandlerId: null };
      this.createFiles = [];
      this.createUploading = false;
    },
    onCreateTargetChange(val) {
      if (val === 'SECONDARY_HANDLER') this.ensureSecondaryHandlers();
      else this.createForm.secondaryHandlerId = null;
    },
    handlerLabel(h) {
      if (!h) return '-';
      return h.displayName && h.displayName !== h.username ? `${h.displayName} / ${h.username}` : h.username;
    },
    async ensureSecondaryHandlers() {
      if (this.secondaryHandlersLoading || (this.secondaryHandlers && this.secondaryHandlers.length > 0)) return;
      this.secondaryHandlersLoading = true;
      try {
        this.secondaryHandlers = await listSecondaryHandlers();
      } finally {
        this.secondaryHandlersLoading = false;
      }
    },
    onResubmitClose() {
      try {
        if (this.$refs.resubmitUploader && this.$refs.resubmitUploader.cancelUploads) this.$refs.resubmitUploader.cancelUploads();
      } catch (e) {}
      this.resubmitContent = '';
      this.resubmitFiles = [];
      this.resubmitUploading = false;
    },
    onDisputeClose() {
      try {
        if (this.$refs.disputeUploader && this.$refs.disputeUploader.cancelUploads) this.$refs.disputeUploader.cancelUploads();
      } catch (e) {}
      this.disputeContent = '';
      this.disputeFiles = [];
      this.disputeUploading = false;
    },
    onUploaded(f) {
      const url = `/api/files/${f.id}`;
      const line = `[${f.originalName}](${url})`;
      this.createForm.content = (this.createForm.content || '').trim() ? `${this.createForm.content}\n${line}` : line;
      this.$message.success(`${this.$t('common.uploaded')}: #${f.id}`);
    },
    async submitCreate() {
      if (!this.createForm.title) return this.$message.error(this.$t('ticket.requiredTitle'));
      if (!this.createForm.content) return this.$message.error(this.$t('ticket.requiredContent'));
      if (this.createForm.targetType === 'SECONDARY_HANDLER' && !this.createForm.secondaryHandlerId) {
        return this.$message.error(this.$t('ticket.requiredSecondaryHandler'));
      }
      try {
        if (this.$refs.createUploader && this.$refs.createUploader.isUploading && this.$refs.createUploader.isUploading()) {
          return this.$message.warning(this.$t('common.uploading'));
        }
      } catch (e) {}
      this.createLoading = true;
      try {
        const payload = {
          title: this.createForm.title,
          content: this.createForm.content,
          fileIds: this.createFileIds
        };
        if (this.createForm.targetType === 'SECONDARY_HANDLER') {
          payload.secondaryHandlerId = this.createForm.secondaryHandlerId;
        }
        const r = await createCase(payload);
        try {
          if (this.$refs.createUploader && this.$refs.createUploader.markCommitted) this.$refs.createUploader.markCommitted(this.createFileIds);
        } catch (e) {}
        this.createDialog = false;
        this.onCreateClose();
        this.$message.success(`${this.$t('ticket.ticketNo')} ${r.caseNo}`);
        await this.loadStats();
        await this.load();
      } finally {
        this.createLoading = false;
      }
    },
    async openDetail(row) {
      if (!row || !row.caseNo) return;
      this.detailDialog = true;
      const q = { ...this.$route.query, caseNo: row.caseNo };
      if (this.$route.query.caseNo !== row.caseNo) {
        this.$router.replace({ path: '/tickets', query: q }).catch(() => {});
      }
      await this.loadDetail(row.caseNo);
      // Viewing detail clears "new closed/dispute decision" red-dot for this case (if any).
      try {
        await markCaseSeen(row.caseNo);
        // Update local list row to avoid waiting for refresh.
        this.items = (this.items || []).map((x) => (x && x.caseNo === row.caseNo ? { ...x, hasNewNotice: false } : x));
        await this.loadStats();
        this.$store.dispatch('refreshTicketRedDot');
      } catch (e) {}
    },
    onDetailClose() {
      const q = { ...this.$route.query };
      delete q.caseNo;
      this.$router.replace({ path: '/tickets', query: q });
      this.detail = null;
      this.resubmitDialog = false;
      this.disputeDialog = false;
    },
    async loadDetail(caseNo) {
      this.detailLoading = true;
      try {
        this.detail = await getCaseDetail(caseNo);
      } finally {
        this.detailLoading = false;
      }
    },
    async cancel() {
      if (!this.detail) return;
      this.actionLoading = true;
      try {
        await cancelCase(this.detail.caseNo);
        await this.loadDetail(this.detail.caseNo);
        await this.loadStats();
        await this.load();
        try {
          await this.$store.dispatch('refreshTicketNeedMore');
        } catch (e) {}
      } finally {
        this.actionLoading = false;
      }
    },
    async doUrgeCase() {
      if (!this.detail || !this.canUrgeCase) return;
      this.urging = true;
      try {
        await urgeCase(this.detail.caseNo);
        this.$message.success(this.$t('ticket.urgeSent'));
      } finally {
        this.urging = false;
      }
    },
    async doUrgeDispute() {
      if (!this.detail || !this.canUrgeDispute) return;
      this.urging = true;
      try {
        await urgeDispute(this.detail.caseNo);
        this.$message.success(this.$t('ticket.urgeSent'));
      } finally {
        this.urging = false;
      }
    },
    async resubmit() {
      if (!this.detail) return;
      if (!this.resubmitContent) return this.$message.error(this.$t('ticket.requiredContent'));
      try {
        if (this.$refs.resubmitUploader && this.$refs.resubmitUploader.isUploading && this.$refs.resubmitUploader.isUploading()) {
          return this.$message.warning(this.$t('common.uploading'));
        }
      } catch (e) {}
      this.actionLoading = true;
      try {
        await resubmitCase(this.detail.caseNo, { content: this.resubmitContent, fileIds: this.resubmitFileIds });
        try {
          if (this.$refs.resubmitUploader && this.$refs.resubmitUploader.markCommitted) this.$refs.resubmitUploader.markCommitted(this.resubmitFileIds);
        } catch (e) {}
        this.resubmitDialog = false;
        this.onResubmitClose();
        await this.loadDetail(this.detail.caseNo);
        await this.loadStats();
        await this.load();
        try {
          await this.$store.dispatch('refreshTicketNeedMore');
        } catch (e) {}
      } finally {
        this.actionLoading = false;
      }
    },
    async dispute() {
      if (!this.detail) return;
      if (!this.disputeContent) return this.$message.error(this.$t('ticket.requiredContent'));
      try {
        if (this.$refs.disputeUploader && this.$refs.disputeUploader.isUploading && this.$refs.disputeUploader.isUploading()) {
          return this.$message.warning(this.$t('common.uploading'));
        }
      } catch (e) {}
      this.actionLoading = true;
      try {
        await createDispute(this.detail.caseNo, { content: this.disputeContent, fileIds: this.disputeFileIds });
        try {
          if (this.$refs.disputeUploader && this.$refs.disputeUploader.markCommitted) this.$refs.disputeUploader.markCommitted(this.disputeFileIds);
        } catch (e) {}
        this.disputeDialog = false;
        this.onDisputeClose();
        await this.loadDetail(this.detail.caseNo);
        await this.loadStats();
        await this.load();
      } finally {
        this.actionLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}
.stats-tile {
  position: relative;
  padding: 12px 14px;
  cursor: pointer;
  transition: transform 0.14s ease;
}
.stats-tile__badge {
  position: absolute;
  top: 10px;
  right: 10px;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(239, 68, 68, 0.95);
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  box-shadow: 0 5px 12px rgba(239, 68, 68, 0.16);
}
.stats-tile:hover {
  transform: translateY(-1px);
}
.stats-tile--active {
  border-color: rgba(37, 99, 235, 0.35);
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.12);
}
.stats-tile__label {
  font-size: 12px;
}
.stats-tile__value {
  margin-top: 8px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 20px;
}

.status-pill-wrap {
  position: relative;
  display: inline-block;
}
.tag-pill--with-dot {
  padding-right: 18px;
}
.status-pill-dot {
  position: absolute;
  top: 3px;
  right: 5px;
  width: 7px;
  height: 7px;
  border-radius: 99px;
  background: rgba(239, 68, 68, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.95);
}

.chat {
  display: grid;
  gap: 10px;
}
.chat__row {
  display: flex;
}
.chat__row--mine {
  justify-content: flex-end;
}
.chat__row--other {
  justify-content: flex-start;
}
.chat__bubble {
  width: min(720px, 100%);
  border-radius: 16px;
  padding: 12px;
  border: 1px solid rgba(226, 232, 240, 0.9);
}
.chat__bubble--mine {
  background: rgba(37, 99, 235, 0.10);
  border-color: rgba(37, 99, 235, 0.20);
}
.chat__bubble--other {
  background: rgba(255, 255, 255, 0.55);
}
.chat__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}
.chat__name {
  font-weight: 600;
}
.chat__text {
  margin-top: 8px;
  white-space: pre-wrap;
  line-height: 1.6;
}
.chat__separator {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0;
}
.chat__separatorLine {
  flex: 1;
  height: 1px;
  background: rgba(226, 232, 240, 0.95);
}
.chat__separatorTag {
  white-space: nowrap;
}
.create-target-hint {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
}

@media (max-width: 980px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
